import classNames from 'classnames';
import * as React from 'react';
import Icon, { IconSize } from 'components/styleguide/icon/Icon';

const styles = require('./IconButton.module.scss');

export type IconButtonContext = 'talent' | 'company' | 'neutral' | 'semantic';
export type IconButtonType = 'primary' | 'secondary' | 'terceary';
export type IconButtonSize = 'S' | 'M';

interface IconButtonProps {
  'aria-label': string;
  'data-testid'?: string;
  className?: string;
  context?: IconButtonContext;
  disabled?: boolean;
  href?: string;
  iconName: string;
  id?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  rel?: string;
  size?: IconButtonSize;
  target?: string;
  type?: IconButtonType;
  typeAttr?: 'button' | 'submit' | 'reset';
}

const SIZE_TO_ICON_SIZE: Record<IconButtonSize, IconSize> = {
  S: 'sm',
  M: 'md',
};

const IconButton: React.FC<IconButtonProps> = ({
  'aria-label': ariaLabel,
  'data-testid': testId,
  className,
  context = 'neutral',
  disabled = false,
  href,
  iconName,
  id,
  onClick,
  onKeyDown,
  rel,
  size = 'M',
  target,
  type = 'primary',
  typeAttr = 'button',
}) => {
  const buttonClasses = classNames(
    styles['ui-icon-button'],
    styles[`ui-icon-button--context-${context}`],
    styles[`ui-icon-button--type-${type}`],
    styles[`ui-icon-button--size-${size.toLowerCase()}`],
    className
  );

  const commonProps = {
    'aria-label': ariaLabel,
    'data-testid': testId,
    'data-context': context,
    'data-type': type,
    'data-size': size,
    className: buttonClasses,
    id,
  };

  const iconNode = <Icon name={iconName} size={SIZE_TO_ICON_SIZE[size]} aria-hidden="true" />;

  if (href) {
    const linkRel = rel || (target === '_blank' ? 'noopener noreferrer' : undefined);
    if (disabled) {
      return (
        <a
          {...commonProps}
          aria-disabled="true"
          tabIndex={-1}
          onClick={(event) => event.preventDefault()}
          onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLAnchorElement>}
        >
          {iconNode}
        </a>
      );
    }
    return (
      <a
        href={href}
        target={target}
        rel={linkRel}
        onClick={onClick as React.MouseEventHandler<HTMLAnchorElement> | undefined}
        {...commonProps}
        onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLAnchorElement>}
      >
        {iconNode}
      </a>
    );
  }

  return (
    <button
      type={typeAttr}
      onClick={onClick}
      disabled={disabled}
      {...commonProps}
      onKeyDown={onKeyDown as React.KeyboardEventHandler<HTMLButtonElement>}
    >
      {iconNode}
    </button>
  );
};

export default IconButton;
